import { useEffect, useCallback } from "react";
import { proxy, useSnapshot } from "valtio";
import {
  db,
  useUserSettings,
  upsertUserSettings,
  type ThemeOption,
  type StyleVariationOption,
} from "@/lib/instantdb";

// Re-export types for convenience
export type { ThemeOption, StyleVariationOption };

/**
 * Settings store - synced to InstantDB when logged in, falls back to localStorage
 */
interface SettingsStore {
  theme: ThemeOption;
  styleVariation: StyleVariationOption;
  /** Whether settings have been loaded from InstantDB */
  syncedFromCloud: boolean;
  /** Whether a sync operation is in progress */
  syncing: boolean;
  // Privacy settings
  analyticsEnabled: boolean;
}

export const settingsStore = proxy<SettingsStore>({
  theme: "system",
  styleVariation: "engaging",
  syncedFromCloud: false,
  syncing: false,
  // Privacy defaults
  analyticsEnabled: true,
});

// ============ Local Storage Keys ============

const THEME_KEY = "vizi-theme";
const STYLE_VARIATION_KEY = "vizi-style-variation";
const ANALYTICS_ENABLED_KEY = "vizi-analytics-enabled";

// ============ Theme Application ============

/** Apply theme to DOM */
function applyTheme(theme: ThemeOption) {
  const isDark =
    theme === "dark" ||
    (theme === "system" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);
  document.documentElement.classList.toggle("dark", isDark);
}

// ============ Settings Actions ============

/** Set theme - updates local store, localStorage, and syncs to InstantDB if logged in */
export async function setTheme(theme: ThemeOption, userId?: string) {
  settingsStore.theme = theme;
  localStorage.setItem(THEME_KEY, theme);
  applyTheme(theme);

  // Sync to InstantDB if logged in
  if (userId) {
    settingsStore.syncing = true;
    try {
      await upsertUserSettings(userId, { theme });
    } catch (error) {
      console.error("Failed to sync theme to cloud:", error);
    } finally {
      settingsStore.syncing = false;
    }
  }
}

/** Set style variation - updates local store, localStorage, and syncs to InstantDB */
export async function setStyleVariation(
  styleVariation: StyleVariationOption,
  userId?: string,
) {
  settingsStore.styleVariation = styleVariation;
  localStorage.setItem(STYLE_VARIATION_KEY, styleVariation);

  // Sync to InstantDB if logged in
  if (userId) {
    settingsStore.syncing = true;
    try {
      await upsertUserSettings(userId, { styleVariation });
    } catch (error) {
      console.error("Failed to sync style variation to cloud:", error);
    } finally {
      settingsStore.syncing = false;
    }
  }
}

// ============ Privacy Settings Actions ============

export function setAnalyticsEnabled(enabled: boolean) {
  settingsStore.analyticsEnabled = enabled;
  localStorage.setItem(ANALYTICS_ENABLED_KEY, String(enabled));
}

/** Initialize settings from localStorage (call before React renders) */
export function initializeSettings() {
  // Load theme from localStorage
  const savedTheme = localStorage.getItem(THEME_KEY) as ThemeOption | null;
  if (savedTheme && ["light", "dark", "system"].includes(savedTheme)) {
    settingsStore.theme = savedTheme;
    applyTheme(savedTheme);
  } else {
    applyTheme("system");
  }

  // Load style variation from localStorage
  const savedVariation = localStorage.getItem(
    STYLE_VARIATION_KEY,
  ) as StyleVariationOption | null;
  if (
    savedVariation &&
    ["clean", "engaging", "cinematic", "hype"].includes(savedVariation)
  ) {
    settingsStore.styleVariation = savedVariation;
  }

  // Load privacy settings from localStorage
  const savedAnalytics = localStorage.getItem(ANALYTICS_ENABLED_KEY);
  if (savedAnalytics !== null) {
    settingsStore.analyticsEnabled = savedAnalytics === "true";
  }

  // Listen for system preference changes
  window
    .matchMedia("(prefers-color-scheme: dark)")
    .addEventListener("change", (e) => {
      if (settingsStore.theme === "system") {
        document.documentElement.classList.toggle("dark", e.matches);
      }
    });
}

// ============ React Hook ============

/**
 * Hook to use settings with InstantDB sync
 * - Loads from localStorage immediately
 * - Syncs from InstantDB when user is logged in
 * - Updates both localStorage and InstantDB on changes
 */
export function useSettings() {
  const { user } = db.useAuth();
  const userId = user?.id;
  const { data, isLoading } = useUserSettings(userId);
  const settings = useSnapshot(settingsStore);

  // Sync from InstantDB when data arrives
  useEffect(() => {
    if (
      !isLoading &&
      data?.user_settings?.[0] &&
      !settingsStore.syncedFromCloud
    ) {
      const cloudSettings = data.user_settings[0];

      // Apply cloud settings (they take precedence)
      if (cloudSettings.theme) {
        const theme = cloudSettings.theme as ThemeOption;
        settingsStore.theme = theme;
        localStorage.setItem(THEME_KEY, theme);
        applyTheme(theme);
      }

      if (cloudSettings.styleVariation) {
        const variation = cloudSettings.styleVariation as StyleVariationOption;
        settingsStore.styleVariation = variation;
        localStorage.setItem(STYLE_VARIATION_KEY, variation);
      }

      settingsStore.syncedFromCloud = true;
    }
  }, [isLoading, data]);

  // Reset sync status when user logs out
  useEffect(() => {
    if (!userId) {
      settingsStore.syncedFromCloud = false;
    }
  }, [userId]);

  // Create bound setters that include userId
  const boundSetTheme = useCallback(
    (theme: ThemeOption) => setTheme(theme, userId),
    [userId],
  );

  const boundSetStyleVariation = useCallback(
    (variation: StyleVariationOption) => setStyleVariation(variation, userId),
    [userId],
  );

  return {
    theme: settings.theme,
    styleVariation: settings.styleVariation,
    syncedFromCloud: settings.syncedFromCloud,
    syncing: settings.syncing,
    isLoading,
    setTheme: boundSetTheme,
    setStyleVariation: boundSetStyleVariation,
    // Privacy settings
    analyticsEnabled: settings.analyticsEnabled,
    setAnalyticsEnabled,
  };
}
